/**
 * De sollicitatiebalk die meereist zodra de binnenkomer uit beeld is.
 *
 * Twee grenzen, geen scrollpositie in pixels: de balk komt op als de binnenkomer voorbij
 * is, en gaat weer weg als het sollicitatieblok in beeld komt. Een knop naar iets wat je
 * al ziet is ruis.
 *
 * De balk staat met `hidden` in de HTML en wordt hier pas zichtbaar gemaakt. Zonder JS is
 * hij er dus niet -- dat mag, want dezelfde knop staat boven- en onderaan de pagina. Zo
 * blijft er ook geen balk staan die nergens op reageert als er iets misgaat.
 */

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default function initVacancyApplyBar() {
  const bar = document.querySelector('[data-vacancy-apply-bar]');
  const hero = document.querySelector('[data-vacancy-hero]');
  const target = document.querySelector('#solliciteren');

  // #solliciteren is de sectie van het sollicitatieblok, en dat blok staat niet
  // vergrendeld op een vacature (zie resources/posttypes/posttype_vacancies.php) -- een
  // redacteur kan het weghalen. Zonder target heeft "voorbij het formulier" geen betekenis
  // meer: bijFormulier hieronder zou dan nooit meer waar worden en de balk zou na de
  // binnenkomer voorgoed blijven staan. Beter geen balk dan een balk die niet meer weet
  // wanneer hij weg moet.
  if (! bar || ! hero || ! target) {
    return;
  }

  bar.hidden = false;

  let voorbijHero = false;
  let bijFormulier = false;

  function pas() {
    bar.dataset.vacancyApplyBarState = voorbijHero && ! bijFormulier ? 'visible' : 'hidden';
  }

  // Beide grenzen via onToggle, en niet de mix van onEnter/onLeaveBack die hier eerst
  // stond: die twee draaien op dezelfde interne toggle-detectie, dus de naam van de
  // callback was niet het echte probleem. Het echte probleem: ScrollTrigger vuurt zo'n
  // callback alleen bij een gedetecteerde overgang -- nooit nog eens voor de stand die al
  // klopt op het moment van aanmaken. Komt een lezer via een diepe link, of herstelt de
  // browser bij een herlaad een scrollpositie voorbij de binnenkomer, dan is er voor
  // ScrollTrigger niets "overgegaan" en blijft voorbijHero op zijn startwaarde false staan
  // tot de lezer terug over de grens scrolt en weer verder.
  //
  // `end: 'max'` geeft de binnenkomer-trigger een écht bereik (tot de onderkant van de
  // pagina) in plaats van de standaard "100% 0" van zijn eigen hoogte -- zonder eigen
  // `end` was dat bereik zo goed als 0px, en dan is `.isActive` nooit bruikbaar, ook niet
  // ná een overgang. Maar `end: 'max'` rekent met de paginahoogte op het moment van
  // aanmaken, en die is bij het laden vaak nog te klein (nog niet alle content eronder is
  // ingevuld) -- vlak na het aanmaken is `.isActive` daardoor zelf nog onbetrouwbaar, ook
  // al staat de lezer al voorbij de binnenkomer. Vandaar de seed via een eigen
  // geometrie-check (dezelfde grens als `start: 'bottom top'`, maar dan met de actuele,
  // live boundingrect) in plaats van via `.isActive`.
  //
  // Bij het sollicitatieblok speelt dat niet: start én end liggen daar vast aan het
  // element zelf (`top bottom` / `bottom top`), niet aan de totale paginahoogte, dus
  // `.isActive` is daar al vanaf het aanmaken betrouwbaar.
  const heroTrigger = ScrollTrigger.create({
    trigger: hero,
    start: 'bottom top',
    end: 'max',
    onToggle: (self) => { voorbijHero = self.isActive; pas(); },
  });
  voorbijHero = hero.getBoundingClientRect().bottom <= 0;

  // target bestaat gegarandeerd: de vroege return hierboven vangt het geval af dat hij
  // ontbreekt.
  const targetTrigger = ScrollTrigger.create({
    trigger: target,
    start: 'top bottom',
    end: 'bottom top',
    onToggle: (self) => { bijFormulier = self.isActive; pas(); },
  });
  bijFormulier = targetTrigger.isActive;

  pas();
}
